import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Stars } from "@/components/travel/Stars";
import { Plane, Package, ArrowRight, MessageSquare } from "lucide-react";
import PostcardButton from "@/components/travel/PostcardButton";

export default function Dashboard() {
  const { user } = useAuth();
  const [trips, setTrips] = useState([]);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [t, c] = await Promise.all([api.get("/trips/mine"), api.get("/contacts/mine")]);
        setTrips(t.data); setContacts(c.data);
      } catch (err) {
        console.error("dashboard load failed", err);
      }
    })();
  }, []);

  if (!user) return <div className="max-w-md mx-auto p-8 text-center text-slate-400">Connectez-vous.</div>;

  const asBuddy = contacts.filter(c => c.i_am_buddy);
  const asTraveler = contacts.filter(c => !c.i_am_buddy);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row md:items-end gap-4 md:justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold">Bonjour, {user.name}</h1>
          <div className="flex items-center gap-2 mt-1 text-sm text-slate-400">
            <Stars value={user.rating_avg}/> <span>{user.rating_avg}/5 · {user.rating_count} avis</span>
          </div>
        </div>
        <Link to="/wizard" data-testid="dash-new-trip" className="btn-coral px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2 self-start md:self-auto">
          <Plane size={14}/> Publier un trajet
        </Link>
      </div>

      <div className="grid md:grid-cols-3 gap-3 mb-8">
        <Stat label="Trajets publiés" value={trips.length} />
        <Stat label="Contacts (buddy)" value={asBuddy.length} />
        <Stat label="Demandes reçues (voyageur)" value={asTraveler.length} />
      </div>

      <section className="mb-10">
        <h2 className="font-display text-xl font-bold mb-3">Mes trajets</h2>
        <div className="grid gap-2">
          {trips.map((t) => (
            <div key={t.trip_id} className="glass rounded-xl p-4 flex flex-wrap gap-3 items-center justify-between">
              <div>
                <div className="text-xs text-slate-400">{t.mode} · {new Date(t.depart_at).toLocaleString("fr-FR")}</div>
                <div className="font-semibold text-sm">{t.origin.label} → {t.destination.label}</div>
              </div>
              <div className="flex items-center gap-3">
                <PostcardButton trip={t} onGenerated={(u)=>setTrips(ts=>ts.map(x=>x.trip_id===t.trip_id?{...x,postcard_url:u}:x))}/>
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">{t.status}</span>
              </div>
            </div>
          ))}
          {trips.length === 0 && <div className="text-slate-500 text-sm">Aucun trajet publié.</div>}
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-bold mb-3">Mes conversations</h2>
        <div className="grid gap-2">
          {contacts.map((c) => (
            <Link to={`/messages/${c.contact_id}`} data-testid={`dash-chat-${c.contact_id}`} key={c.contact_id} className="glass rounded-xl p-4 flex items-center justify-between hover:border-slate-600 border border-slate-800 transition">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-xs">{c.counterpart?.name?.[0] || "?"}</div>
                <div>
                  <div className="text-sm font-semibold flex items-center gap-2">{c.counterpart?.name} <Package size={12} className="text-slate-500"/></div>
                  <div className="text-xs text-slate-400">{c.trip?.origin?.label} → {c.trip?.destination?.label}</div>
                </div>
              </div>
              <MessageSquare size={16} className="text-slate-500"/>
            </Link>
          ))}
          {contacts.length === 0 && <div className="text-slate-500 text-sm">Aucune conversation.</div>}
        </div>
      </section>
    </div>
  );
}

function Stat({label, value}) {
  return (
    <div className="glass rounded-xl p-4">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="font-display text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
